/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { ModalController } from '@ionic/angular';
import { Component, OnInit } from '@angular/core';
import { AppService } from '@core/services/app/app.service';
import { HelpService } from '@services/help/help.service';
import { HelpLinks } from '@core/models';
import { CustomerService } from '@core/services/customer/customer.service';
import { CustomerDTO } from '@core/modules/api/models';
import { DestroyDirective } from '@shared/components/destroy-abstract/destroy.abstract';
import { finalize, takeUntil } from 'rxjs';
import { NavigatorService } from '@core/services/navigator/navigator.service';
import { DeviceService } from '@core/services/device/device.service';
import { AuthService } from '@core/services/auth/auth.service';
import { UiService } from '@core/services/ui/ui.service';
import { PositionService } from '@core/services/position/position.service';

@Component({
  selector: 'app-modal-state',
  templateUrl: './modal-state.component.html',
  styleUrls: ['./modal-state.component.scss']
})
export class ModalStateComponent extends DestroyDirective implements OnInit {
  readonly helps: HelpLinks[] = ['faq', 'helpline', 'live_chat'];

  activeHelps: HelpLinks[] = [];

  logoUrl: string;

  customer: CustomerDTO;

  reservedAreaBtnVisible: boolean;

  logoutBtnVisible: boolean;

  private userCustomer: CustomerDTO;

  private positionCustomer: CustomerDTO;

  constructor(
    private modalController: ModalController,
    private appService: AppService,
    private customerService: CustomerService,
    private positionService: PositionService,
    private navigator: NavigatorService,
    private authService: AuthService,
    private uiService: UiService,
    public helpService: HelpService
  ) {
    super();
  }

  ngOnInit() {
    this.setHelpsActive();
    this.setLogoUrl();
    this.setReservedAreaBtnVisible();
    this.setLogoutBtnVisible();
    this.watchCustomer();
    this.watchPosition();
  }

  navigateToReservedArea(): void {
    this.close();
    this.navigator.exitAllowedOnce = true;
    this.navigator.navigateToReservedArea();
  }

  logout(): void {
    this.close();
    this.uiService.showAppLoader();

    this.authService
      .logout()
      .pipe(finalize(() => this.uiService.hideAppLoader()))
      .subscribe(() => {
        this.navigator.exitAllowedOnce = true;
        this.navigator.navigateToEnter();
      });
  }

  async close(): Promise<void> {
    await this.modalController.dismiss();
  }

  private watchCustomer(): void {
    this.customerService
      .getCustomerWhenChange()
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.userCustomer = res;
        this.setCustomer();
      });
  }

  private watchPosition(): void {
    this.positionService
      .getPositionWhenChange({ whenExists: false })
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.positionCustomer = res && res.getCustomer();
        this.setCustomer();
      });
  }

  private setCustomer(): void {
    if (this.positionCustomer) {
      this.customer = { ...this.positionCustomer };
    } else if (this.userCustomer) {
      this.customer = { ...this.userCustomer };
    } else {
      this.customer = undefined;
    }
  }

  private setHelpsActive(): void {
    this.activeHelps = this.helps.filter(code =>
      this.helpService.isLinkActive(code)
    );
  }

  private setLogoUrl(): void {
    this.logoUrl =
      this.appService.getConfig().theme.bannerUrl ||
      this.appService.getConfig().theme.logoUrl;
  }

  private setReservedAreaBtnVisible(): void {
    this.reservedAreaBtnVisible = this.appService.isStandalone();
  }

  private setLogoutBtnVisible(): void {
    this.logoutBtnVisible =
      this.appService.getConfig().security.logoutEnabled === true;
  }
}
